/**
 * Keycloak authentication for the AI-Search Tools frontend (roadmap#97).
 *
 * The tool is restricted to users holding the SUPER_ADMIN realm role in the
 * `artstash-app` realm. Configuration is baked at build time from
 * REACT_APP_KEYCLOAK_URL / REACT_APP_KEYCLOAK_REALM /
 * REACT_APP_KEYCLOAK_CLIENT_ID (see env/dev and env/prod).
 *
 * When those vars are absent (plain local development), Keycloak is skipped
 * entirely — this matches the backend's explicit AI_TOOLS_AUTH_ENABLED=false
 * local kill-switch. Deployed images always bake the vars in.
 */
import Keycloak from 'keycloak-js';

export const SUPER_ADMIN_ROLE = 'SUPER_ADMIN';

let keycloak: Keycloak | null = null;

export interface AuthInitResult {
    authenticated: boolean;
}

export function isAuthConfigured(): boolean {
    return Boolean(
        process.env.REACT_APP_KEYCLOAK_URL &&
        process.env.REACT_APP_KEYCLOAK_REALM &&
        process.env.REACT_APP_KEYCLOAK_CLIENT_ID
    );
}

/**
 * Initialize Keycloak with login-required: unauthenticated users are
 * redirected to the realm login page before the app renders.
 */
export async function initAuth(): Promise<AuthInitResult> {
    keycloak = new Keycloak({
        url: process.env.REACT_APP_KEYCLOAK_URL as string,
        realm: process.env.REACT_APP_KEYCLOAK_REALM as string,
        clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID as string,
    });

    const authenticated = await keycloak.init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
    });

    keycloak.onTokenExpired = () => {
        keycloak?.updateToken(30).catch(() => {
            // Refresh token expired too — force a fresh login.
            keycloak?.login();
        });
    };

    return { authenticated };
}

function realmRoles(): string[] {
    const parsed = keycloak?.tokenParsed as
        | { realm_access?: { roles?: unknown } }
        | undefined;
    const roles = parsed?.realm_access?.roles;
    return Array.isArray(roles)
        ? roles.filter((role): role is string => typeof role === 'string')
        : [];
}

export function hasSuperAdminRole(): boolean {
    return realmRoles().includes(SUPER_ADMIN_ROLE);
}

export function getUsername(): string {
    const parsed = keycloak?.tokenParsed as
        | { preferred_username?: string; email?: string; sub?: string }
        | undefined;
    return parsed?.preferred_username || parsed?.email || parsed?.sub || 'unknown user';
}

/**
 * Return a fresh access token, refreshing it when it expires within 30s.
 * Returns null when Keycloak is not in use (unconfigured local dev).
 */
export async function getToken(): Promise<string | null> {
    if (!keycloak) {
        return null;
    }
    try {
        await keycloak.updateToken(30);
    } catch {
        // Session is no longer refreshable — re-authenticate.
        await keycloak.login();
        return null;
    }
    return keycloak.token ?? null;
}

export function logout(): void {
    keycloak?.logout({ redirectUri: window.location.origin });
}

/** Append the access token as a `token` query parameter (WebSockets). */
export function appendTokenParam(url: string, token: string | null): string {
    if (!token) {
        return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
}
